/**
 * VersionSection - Card version selector for deck cards
 *
 * Allows switching between different printings of the same card.
 * Shows owned printings first with quantity indicators.
 * Displays set code, collector number, and owned count.
 */
import clsx from 'clsx';
import type { ChangeEvent, ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { IoCheckmark, IoChevronDown, IoSwapHorizontal } from 'react-icons/io5';

import { useUpdateDeckCardMutation } from '@/features/decks/hooks/api';
import { useAllOwnedPrintingsForName } from '@/hooks/useCollectionAvailability';
import useCardIndex from '@/hooks/useCardIndex';
import type { MinimalCard } from '@/services/cardDatabase';

// ============================================================================
// Types
// ============================================================================

export interface VersionSectionProps {
  /** The deck card's ID */
  deckCardId: string;
  /** The deck's ID */
  deckId: string;
  /** Current scryfall ID (current printing) */
  scryfallId: string;
  /** Card name (for looking up printings) */
  cardName: string;
  /** Whether the deck is locked */
  isLocked?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export default function VersionSection({
  deckCardId,
  deckId,
  scryfallId,
  cardName,
  isLocked = false,
}: VersionSectionProps): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get card database functions
  const { getAllPrintingsByName, getCardById } = useCardIndex();

  // Get owned printings for this card
  const ownedPrintings = useAllOwnedPrintingsForName(cardName.toLowerCase());

  // Mutation for updating the card version
  const updateCardMutation = useUpdateDeckCardMutation();

  // Get current card data
  const currentCard = useMemo(() => getCardById(scryfallId), [getCardById, scryfallId]);

  // Get all printings sorted: owned first (by quantity), then alphabetically by set
  const allPrintings = useMemo(() => {
    const printings = getAllPrintingsByName(cardName);

    return printings.sort((a, b) => {
      const aOwned = ownedPrintings.get(a.id) ?? 0;
      const bOwned = ownedPrintings.get(b.id) ?? 0;

      // Owned printings first
      if (aOwned > 0 && bOwned === 0) return -1;
      if (aOwned === 0 && bOwned > 0) return 1;

      // Among owned, sort by quantity descending
      if (aOwned !== bOwned) return bOwned - aOwned;

      // Then alphabetically by set
      return a.set.localeCompare(b.set);
    });
  }, [cardName, getAllPrintingsByName, ownedPrintings]);

  // Handle version change
  const handleVersionChange = useCallback(
    (newScryfallId: string) => {
      if (newScryfallId === scryfallId || isLocked) return;

      const newCard = getCardById(newScryfallId);
      updateCardMutation.mutate({
        deckId,
        cardId: deckCardId,
        data: {
          scryfall_id: newScryfallId,
          card_name: newCard?.name ?? cardName,
        },
      });
      setIsExpanded(false);
    },
    [scryfallId, isLocked, getCardById, updateCardMutation, deckId, deckCardId, cardName],
  );

  // Handle select element change
  const handleSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      handleVersionChange(e.target.value);
    },
    [handleVersionChange],
  );

  // Don't render if only one printing exists
  if (allPrintings.length <= 1) {
    return null;
  }

  const isPending = updateCardMutation.isPending;
  const isOwned = ownedPrintings.has(scryfallId);
  const ownedCount = allPrintings.filter((p) => ownedPrintings.has(p.id)).length;

  return (
    <section className="border-t border-zinc-800 px-6 py-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-zinc-200">Version</h3>
        {ownedCount > 0 && (
          <span className="text-xs text-zinc-500">
            {ownedCount} owned printing{ownedCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Current version display */}
      <div className="mb-3 flex items-center gap-3">
        {/* Mini thumbnail */}
        <div className="h-12 w-9 shrink-0 overflow-hidden rounded shadow-md">
          {currentCard?.image_uris?.small ? (
            <img src={currentCard.image_uris.small} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-zinc-800">
              <span className="text-xs text-zinc-600">?</span>
            </div>
          )}
        </div>

        {/* Current version info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-300 uppercase">{currentCard?.set ?? '???'}</span>
            <span className="text-xs text-zinc-500">#{currentCard?.collector_number ?? '?'}</span>
            {isOwned && (
              <span className="text-aether flex items-center gap-0.5 text-xs">
                <IoCheckmark className="h-3 w-3" />
                Owned
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">{allPrintings.length} available printings</p>
        </div>
      </div>

      {/* Version selector - Compact dropdown or expanded list */}
      {isLocked ? (
        <p className="text-xs text-zinc-600 italic">Unlock deck to change version</p>
      ) : isExpanded ? (
        <div className="space-y-1">
          {allPrintings.map((printing) => (
            <PrintingOption
              key={printing.id}
              printing={printing}
              isSelected={printing.id === scryfallId}
              ownedQuantity={ownedPrintings.get(printing.id) ?? 0}
              onSelect={handleVersionChange}
              disabled={isPending}
            />
          ))}
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="mt-2 w-full py-1 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Show less
          </button>
        </div>
      ) : (
        <div>
          {/* Compact select dropdown */}
          <div className="relative">
            <select
              value={scryfallId}
              onChange={handleSelectChange}
              disabled={isPending}
              className={clsx(
                'w-full appearance-none rounded-lg border px-3 py-2 pr-8 text-sm',
                'border-zinc-700 bg-zinc-800/80 text-zinc-200',
                'focus:border-aether focus:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {allPrintings.map((printing) => {
                const owned = ownedPrintings.get(printing.id) ?? 0;
                return (
                  <option key={printing.id} value={printing.id}>
                    {printing.set.toUpperCase()} #{printing.collector_number}
                    {owned > 0 ? ` (${owned} owned)` : ''}
                  </option>
                );
              })}
            </select>
            <IoChevronDown className="pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          </div>

          {/* Show all button */}
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="mt-2 flex w-full items-center justify-center gap-1.5 py-1 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
          >
            <IoSwapHorizontal className="h-3.5 w-3.5" />
            Browse all printings
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {isPending && <p className="mt-2 text-xs text-zinc-500 italic">Updating...</p>}
    </section>
  );
}

// ============================================================================
// PrintingOption Component
// ============================================================================

interface PrintingOptionProps {
  printing: MinimalCard;
  isSelected: boolean;
  ownedQuantity: number;
  onSelect: (scryfallId: string) => void;
  disabled?: boolean;
}

function PrintingOption({ printing, isSelected, ownedQuantity, onSelect, disabled }: PrintingOptionProps): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onSelect(printing.id)}
      disabled={disabled || isSelected}
      className={clsx(
        'flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
        isSelected
          ? 'border-aether/50 bg-aether/10 text-zinc-200'
          : ownedQuantity > 0
            ? 'border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800'
            : 'border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700 hover:bg-zinc-800/50',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {/* Mini thumbnail */}
      <div className="h-10 w-7 shrink-0 overflow-hidden rounded-sm shadow-sm">
        {printing.image_uris?.small ? (
          <img src={printing.image_uris.small} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-zinc-800">
            <span className="text-[10px] text-zinc-600">?</span>
          </div>
        )}
      </div>

      {/* Printing info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase">{printing.set}</span>
          <span className="text-xs text-zinc-500">#{printing.collector_number}</span>
        </div>
      </div>

      {/* Owned indicator */}
      {ownedQuantity > 0 && (
        <span className="text-aether flex items-center gap-1 text-xs">
          <IoCheckmark className="h-3 w-3" />
          {ownedQuantity}
        </span>
      )}

      {/* Selected indicator */}
      {isSelected && <span className="text-aether text-xs">Current</span>}
    </button>
  );
}
